import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/supabase/client';
import { socialKeys } from './queries';

/**
 * Subscribes to Supabase Realtime for new/updated/deleted social posts in a club.
 * Invalidates React Query feed cache when changes occur, so the feed auto-refreshes.
 *
 * Also listens for new reactions and comments to update counts in real time.
 */
export function useRealtimeFeed(clubId: string) {
  const qc = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!clubId) return;

    const channel = supabase
      .channel(`social-feed-${clubId}`)
      // New / updated / deleted posts
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_posts',
          filter: `club_id=eq.${clubId}`,
        },
        (payload) => {
          // Invalidate all feed queries for this club
          qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
          // Also invalidate bento quadrants
          qc.invalidateQueries({ queryKey: ['social', clubId, 'highlights'] });
          qc.invalidateQueries({ queryKey: ['social', clubId, 'links', 'circle'] });

          // If it's an insert, bump the unread indicators
          if (payload.eventType === 'INSERT') {
            qc.invalidateQueries({ queryKey: socialKeys.subscriptions(clubId) });
          }
        },
      )
      // New reactions (for engagement counts)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_reactions',
        },
        () => {
          // Refresh highlights (MostEngaged quadrant)
          qc.invalidateQueries({ queryKey: ['social', clubId, 'highlights'] });
        },
      )
      // New comments
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_comments',
        },
        () => {
          // Refresh any open comment threads
          qc.invalidateQueries({ queryKey: ['social', clubId, 'posts'] });
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn(`[realtime] Feed channel error for ${clubId}`);
        }
      });

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [clubId, qc]);
}

/**
 * Subscribes to Realtime for DM conversations in a club.
 * Invalidates conversation queries on new messages.
 */
export function useRealtimeConversations(clubId: string) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!clubId) return;

    const channel = supabase
      .channel(`social-convos-${clubId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'social_conversation_messages',
        },
        () => {
          qc.invalidateQueries({ queryKey: socialKeys.conversations(clubId) });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clubId, qc]);
}
